import { createContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TourContextValue, TourState, TourStep } from '../types/TourTypes';
import { TourService } from '../services/tourService';
import { TourStatus } from '../enums/tourStatus';
import { useTourObjective } from '../hooks/useTourObjective';
import { useTourNavigation } from '../hooks/useTourNavigation';
import { TourInitialModal } from './TourInitialModal';
import { TourChecklist } from './TourChecklist';
import { TourStepOverlay } from './TourStepOverlay';
import { TourCompletionModal } from './TourCompletionModal';
import { TourCloseConfirmationModal } from './TourCloseConfirmationModal';
import { TourReminderPopover } from './TourReminderPopover';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useTourSettings } from '@/shared/hooks/api/useTourSettings';
import { useUserSettingsByUserId } from '@/shared/hooks/api/useUserSettings';
import { UserSettingsService, UserSettings } from '@/features/Users/services/userSettingsService';
import '../styles/tour-theme.css';

// eslint-disable-next-line react-refresh/only-export-components
export const TourContext = createContext<TourContextValue | null>(null);

interface TourProviderProps {
  children: ReactNode;
}

export const TourProvider = ({ children }: TourProviderProps) => {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { data: tourConfig, isLoading: isLoadingConfig } = useTourSettings({
    enabled: isAuthenticated && !!user
  });
  const { data: userSettings, isLoading: _isLoadingUserSettings } = useUserSettingsByUserId(user?.id);

  const [tourState, setTourState] = useState<TourState>(TourService.loadInitialState(userSettings));
  const [allSteps, setAllSteps] = useState<TourStep[]>([]);
  const [currentStep, setCurrentStep] = useState<TourStep | null>(null);
  const [showOverlay, setShowOverlay] = useState(true);
  const [showInitialModal, setShowInitialModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [showReminderPopover, setShowReminderPopover] = useState(false);

  // Carrega steps quando a configuração está disponível
  useEffect(() => {
    if (tourConfig) {
      setAllSteps(TourService.loadSteps(tourConfig));
    }
  }, [tourConfig]);

  // Sincroniza estado do tour com dados do usuário
  useEffect(() => {
    if (userSettings) {
      const tourMetadata = userSettings.metadata?.tour;
      if (tourMetadata) {
        const completedSteps = TourService.getCompletedSteps(tourMetadata.step, allSteps);
        setTourState(prev => ({
          ...prev,
          completedSteps,
          hasSeenInitialModal: tourMetadata.status === TourStatus.COMPLETED,
          hasStartedBefore: tourMetadata.status === TourStatus.IN_PROGRESS || tourMetadata.status === TourStatus.COMPLETED
        }));
      }
    }
  }, [userSettings, allSteps]);

  const { navigateToStep } = useTourNavigation(tourState.hasStartedBefore);

  // Tour será disparado manualmente pela tela de boas-vindas (EmptyWorkspaceState)
  // após confirmação do trial/plano

  /**
   * Inicia o tour (retoma do step salvo ou começa do primeiro)
   */
  const startTour = useCallback(async () => {
    if (!allSteps.length || !user?.id) return;

    // Busca dados atualizados diretamente da API para garantir o step correto
    let freshUserSettings: UserSettings | null = null;
    try {
      freshUserSettings = await UserSettingsService.getByUserId(user.id);
      // Atualiza o cache do React Query com os dados frescos
      if (freshUserSettings) {
        queryClient.setQueryData(['user_settings_by_user', user.id], freshUserSettings);
      }
    } catch {
      // Fallback para cache se a API falhar
      freshUserSettings = queryClient.getQueryData<UserSettings>(['user_settings_by_user', user.id]) ?? null;
    }

    const tourMetadata = freshUserSettings?.metadata?.tour;
    const rawStepIndex = tourMetadata?.step ?? 0;

    // Tour concluído (ou com step além do último) reinicia do zero; caso contrário
    // retoma do step salvo. Ao reiniciar, zera o progresso PERSISTIDO — senão o
    // checklist (que lê o step direto do banco) e um futuro "retomar" continuariam
    // marcando tudo como concluído mesmo num tour recém-reiniciado (AB#83992).
    const isCompleted =
      tourMetadata?.status === TourStatus.COMPLETED || rawStepIndex >= allSteps.length;
    let savedStepIndex = rawStepIndex;

    if (isCompleted) {
      savedStepIndex = 0;
      try {
        const reset = await UserSettingsService.updateTourMetadata(user.id, {
          step: 0,
          status: TourStatus.IN_PROGRESS
        });
        queryClient.setQueryData(['user_settings_by_user', user.id], reset);
      } catch (error) {
        // Não bloqueia o reinício local, mas a falha precisa ser observável (AB#83992).
        console.error('[Tour] Falha ao resetar o progresso persistido do tour', {
          userId: user.id,
          error
        });
        // Mantém o cache alinhado ao reinício para o checklist (que lê o step do
        // banco via cache) iniciar zerado nesta sessão mesmo sem persistência.
        queryClient.setQueryData<UserSettings | undefined>(
          ['user_settings_by_user', user.id],
          prev =>
            prev
              ? {
                  ...prev,
                  metadata: {
                    ...prev.metadata,
                    tour: { step: 0, status: TourStatus.IN_PROGRESS }
                  }
                }
              : prev
        );
      }
    }

    // Determina qual step iniciar (salvo ou primeiro)
    const stepToStart = allSteps[savedStepIndex] || allSteps[0];

    // Calcula steps completados até o step atual
    const completedSteps = TourService.getCompletedSteps(savedStepIndex, allSteps);

    setShowInitialModal(false);
    setShowOverlay(false);
    setTourState(prev => ({
      ...prev,
      isActive: true,
      hasSeenInitialModal: true,
      hasStartedBefore: true,
      currentStepId: stepToStart.id,
      completedSteps,
      isChecklistVisible: true
    }));

    const delay = navigateToStep(stepToStart, true);

    setTimeout(() => {
      setCurrentStep(stepToStart);
      setShowOverlay(true);
    }, delay);
  }, [allSteps, navigateToStep, user?.id, queryClient]);

  /**
   * Avança para o próximo step
   */
  const nextStep = useCallback(async (completedStepId?: string) => {
    // Se receber stepId, usa ele; senão usa currentStep
    const stepId = completedStepId || currentStep?.id;

    if (!stepId || !user?.id) {
      console.error('[nextStep] Missing stepId or userId');
      return;
    }

    const currentIndex = allSteps.findIndex(s => s.id === stepId);
    if (currentIndex === -1) {
      console.error('[nextStep] Step not found:', stepId);
      return;
    }

    const nextStepData = allSteps[currentIndex + 1];

    if (nextStepData) {
      // Atualiza step na API
      console.log('[nextStep] Updating API - step:', currentIndex + 1);
      await UserSettingsService.updateTourMetadata(user.id, {
        step: currentIndex + 1,
        status: TourStatus.IN_PROGRESS
      });

      // Invalidar cache do userSettings para atualizar contador
      queryClient.invalidateQueries({ queryKey: ['user_settings_by_user', user.id] });

      setTourState(prev => ({
        ...prev,
        currentStepId: nextStepData.id
      }));

      setShowOverlay(true);
      const delay = navigateToStep(nextStepData, false);

      setTimeout(() => {
        setCurrentStep(nextStepData);
      }, delay);
    } else {
      // Tour completado
      console.log('[nextStep] Tour completed - updating API');
      await UserSettingsService.updateTourMetadata(user.id, {
        step: allSteps.length,
        status: TourStatus.COMPLETED
      });

      // Invalidar cache do userSettings para atualizar contador
      queryClient.invalidateQueries({ queryKey: ['user_settings_by_user', user.id] });

      setShowCompletionModal(true);
      setCurrentStep(null);
      setTourState(prev => ({
        ...prev,
        isActive: false,
        currentStepId: null
      }));
    }
  }, [currentStep, allSteps, navigateToStep, user?.id, queryClient]);

  /**
   * Marca um step como completo e avança para o próximo
   */
  const completeStep = useCallback((stepId: string) => {
    console.log('[completeStep] Completing step:', stepId);

    setTourState(prev => {
      const newCompletedSteps = [...prev.completedSteps, stepId];

      return {
        ...prev,
        completedSteps: newCompletedSteps
      };
    });

    setShowOverlay(false);
    setCurrentStep(null);

    // Passa o stepId para nextStep para evitar dependência de currentStep
    setTimeout(() => {
      nextStep(stepId);
    }, 150);
  }, [nextStep]);

  /**
   * Fecha o tour (com confirmação se já iniciado)
   */
  const closeTour = useCallback(async () => {
    if (tourState.isActive) {
      setShowCloseConfirmation(true);
      return;
    }

    setShowInitialModal(false);
    setCurrentStep(null);
    setTourState(prev => ({
      ...prev,
      isActive: false,
      currentStepId: null,
      isChecklistVisible: false
    }));

    setTimeout(() => {
      setShowReminderPopover(true);
    }, 500);
  }, [tourState.isActive]);

  /**
   * Confirma e fecha o tour definitivamente
   */
  const confirmCloseTour = useCallback(async () => {
    // Fechar o tour é uma ação de UI e não pode depender do sucesso da
    // persistência: encerramos o overlay/checklist imediatamente e persistimos
    // o status como best-effort. Se o await ficar antes do reset e a chamada
    // falhar, o tour trava aberto (regressão #83779).
    const wasInProgress = userSettings?.metadata?.tour?.status === TourStatus.IN_PROGRESS;

    setShowCloseConfirmation(false);
    setShowInitialModal(false);
    setCurrentStep(null);
    setTourState(prev => ({
      ...prev,
      isActive: false,
      currentStepId: null,
      isChecklistVisible: false
    }));

    if (wasInProgress) {
      setTimeout(() => {
        setShowReminderPopover(true);
      }, 500);
    }

    if (user?.id) {
      try {
        await UserSettingsService.updateTourMetadata(user.id, {
          status: TourStatus.COMPLETED
        });
      } catch (error) {
        console.error('[Tour] Falha ao persistir encerramento do tour:', error);
      }
    }
  }, [user?.id, userSettings]);

  /**
   * Mostra o modal inicial do tour (usado pela tela de boas-vindas)
   */
  const showTourModal = useCallback(() => {
    // Superadmin entra no dashboard de plataforma (sem org/produtos), o que
    // dispararia o tour de boas-vindas — que é "primeiro acesso da org" e não
    // se aplica a ele. Não há tour de onboarding para superadmin.
    if (user?.isSuperAdmin) {
      return;
    }

    if (!TourService.shouldShowInitialModal(userSettings)) {
      return;
    }

    setTimeout(() => {
      setShowInitialModal(true);
    }, 1000);
  }, [userSettings, user?.isSuperAdmin]);

  /**
   * Reinicia o tour do zero (apenas estado local, não reseta banco)
   * O banco mantém o progresso para permitir retomar de onde parou
   */
  const restartTour = useCallback(async () => {
    TourService.clearSessionCache();

    setTourState({
      isActive: false,
      hasSeenInitialModal: false,
      hasStartedBefore: false,
      currentStepId: null,
      completedSteps: [],
      isChecklistVisible: false
    });
    setShowCompletionModal(false);

    setTimeout(() => {
      setShowInitialModal(true);
    }, 300);
  }, []);

  /**
   * Toggle da visibilidade do checklist
   */
  const toggleChecklist = useCallback(() => {
    setTourState(prev => ({
      ...prev,
      isChecklistVisible: !prev.isChecklistVisible
    }));
  }, []);

  // Hook para monitorar objetivos
  useTourObjective({
    currentStep,
    completedSteps: tourState.completedSteps,
    onComplete: completeStep
  });

  const contextValue: TourContextValue = {
    tourState,
    currentStep,
    allSteps,
    startTour,
    nextStep,
    completeStep,
    closeTour,
    restartTour,
    toggleChecklist,
    showTourModal
  };

  return (
    <TourContext.Provider value={contextValue}>
      {children}

      {/* Apenas renderiza modais se configuração estiver carregada */}
      {!isLoadingConfig && tourConfig && (
        <>
          {/* Modal inicial */}
          {showInitialModal && (
            <TourInitialModal
              config={tourConfig}
              onStart={startTour}
              onClose={closeTour}
            />
          )}

          {/* Checklist flutuante */}
          {tourState.isActive && tourState.isChecklistVisible && (
            <TourChecklist />
          )}

          {/* Overlay da etapa atual */}
          {tourState.isActive && currentStep && showOverlay && (
            <TourStepOverlay
              step={currentStep}
              onHide={() => setShowOverlay(false)}
              onInteraction={(stepId) => {
                // Dispara evento customizado para o useTourObjective
                window.dispatchEvent(new CustomEvent('tour-step-interacted', { detail: { stepId } }));
              }}
            />
          )}

          {/* Modal de conclusão */}
          {showCompletionModal && (
            <TourCompletionModal
              onClose={() => setShowCompletionModal(false)}
            />
          )}

          {/* Modal de confirmação ao fechar */}
          {showCloseConfirmation && (
            <TourCloseConfirmationModal
              onConfirm={confirmCloseTour}
              onCancel={() => setShowCloseConfirmation(false)}
            />
          )}

          {/* Popover de lembrete */}
          <TourReminderPopover
            isOpen={showReminderPopover}
            onClose={() => setShowReminderPopover(false)}
          />
        </>
      )}
    </TourContext.Provider>
  );
};
